import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Flex, H1WithBack, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { IOpsMachineDay } from 'types/opsAnalytics.interface'
import { IOpsMachineRow, IOpsShiftWindow, IOpsStatEntry } from 'types/opsTimelines.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { LOSS_BUCKET_META } from 'pages/Operations/Shared/lossClassifier'
import {
  liveTodayMachineDay,
  OPS_MACHINE_DAYS,
  OPS_TODAY_NOW_LABEL,
  opsDateLabelOf,
} from 'pages/Operations/Shared/opsAnalyticsData'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { BucketLegend, DayStrip } from './DayStrip'
import { LiveTodaySection } from './LiveTodaySection'
import { TimelineHistory } from './TimelineHistory'
import {
  BarFill,
  BarTrack,
  BucketRow,
  BucketRows,
  DangerDot,
  StatCell,
  StatGrid,
  ToneValue,
  TotalsWrap,
} from './Timelines.styled'

const fmtInt = (value: number): string => value.toLocaleString('en-US')

const SHIFT_WINDOWS: IOpsShiftWindow[] = [
  { key: 'day', label: 'Day shift', startHour: 6, windowHours: 12, caption: '06:00 → 18:00' },
  { key: 'night', label: 'Night shift', startHour: 18, windowHours: 12, caption: '18:00 → 06:00' },
  { key: 'h24', label: '24 h', startHour: 6, windowHours: 24, caption: '06:00 → 06:00' },
]

const TODAY_MACHINE_DAYS: IOpsMachineDay[] = Array.from(
  new Set(OPS_MACHINE_DAYS.map((day) => day.machineName)),
)
  .map((machineName) => liveTodayMachineDay(machineName))
  .filter((day): day is IOpsMachineDay => day != null)

const QUICK_DAYS: string[] = TODAY_MACHINE_DAYS.length ? ['Today', 'Yesterday'] : ['Yesterday']

const PICKER_MIN_DATE = new Date(new Date().getFullYear(), 5, 2)

const utilTone = (pct: number): IStatusPill['status'] =>
  pct >= 80 ? 'success' : pct >= 65 ? 'warning' : 'danger'

const machineStats = (day: IOpsMachineDay): IOpsStatEntry[] => [
  { label: 'Utilization', value: `${day.utilizationPct}%` },
  { label: 'Availability', value: `${day.availabilityPct}%` },
  { label: 'Loads', value: `${day.loads}` },
  { label: 'Tonnes', value: `${fmtInt(day.tonnes)} t` },
  { label: 'Fuel added', value: `${day.fuelAddedL} L` },
  { label: 'SMU delta', value: `${day.smuDeltaH} h` },
  { label: 'Breakdowns', value: `${day.breakdownCount}` },
  { label: 'Cost of day', value: `GHS ${fmtInt(day.costOfDay)}` },
]

const machineColumns: IHonestTableColumn<IOpsMachineRow>[] = [
  {
    id: 'machine',
    header: 'Machine',
    sticky: true,
    minWidth: '10rem',
    cell: (item) => (
      <Flex center gap='0.5rem'>
        {item.worst && <DangerDot title='Lowest utilization this day' />}
        <BodyText weight='600'>{item.machineName}</BodyText>
      </Flex>
    ),
  },
  {
    id: 'operator',
    header: 'Operator',
    minWidth: '9rem',
    cell: (item) => <BodyText grey>{item.operator ?? '—'}</BodyText>,
  },
  {
    id: 'utilization',
    header: 'Utilization',
    minWidth: '11rem',
    cell: (item) => (
      <Flex center gap='0.5rem'>
        <BarTrack>
          <BarFill $pct={item.utilizationPct} $tone={utilTone(item.utilizationPct)} />
        </BarTrack>
        <ToneValue $tone={utilTone(item.utilizationPct)}>{item.utilizationPct}%</ToneValue>
      </Flex>
    ),
  },
  {
    id: 'availability',
    header: 'Availability',
    align: 'right',
    cell: (item) => (
      <ToneValue $tone={item.availabilityPct < 80 ? 'danger' : undefined}>
        {item.availabilityPct}%
      </ToneValue>
    ),
  },
  {
    id: 'loads',
    header: 'Loads',
    align: 'right',
    cell: (item) => <BodyText>{item.loads}</BodyText>,
  },
  {
    id: 'tonnes',
    header: 'Tonnes',
    align: 'right',
    cell: (item) => <BodyText>{fmtInt(item.tonnes)}</BodyText>,
  },
  {
    id: 'breakdowns',
    header: 'Breakdowns',
    align: 'right',
    cell: (item) => (
      <ToneValue $tone={item.breakdownCount > 0 ? 'danger' : undefined}>{item.breakdownCount}</ToneValue>
    ),
  },
  {
    id: 'cost',
    header: 'Cost of day',
    align: 'right',
    cell: (item) => <BodyText>{`GHS ${fmtInt(item.costOfDay)}`}</BodyText>,
  },
]

export const MachineTimelines = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [selectedDay, setSelectedDay] = useState('Yesterday')
  const [pickedDate, setPickedDate] = useState<Date | null>(null)
  const [shiftKey, setShiftKey] = useState('h24')
  const win = SHIFT_WINDOWS.find((w) => w.key === shiftKey) ?? SHIFT_WINDOWS[2]
  const selectQuickDay = (label: string) => {
    setSelectedDay(label)
    setPickedDate(null)
  }
  const handlePickDate = (date: Date | null) => {
    setPickedDate(date)
    if (date) setSelectedDay(opsDateLabelOf(date))
  }

  if (!USE_OPS_MOCKS) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <Head1>Machine timelines</Head1>
          <NoData text='Timelines are computed at shift close once connected.' />
        </PageWrapper>
      </>
    )
  }

  if (id) {
    const days = OPS_MACHINE_DAYS.filter((d) => d.machineId === Number(id))
    const day = days.find((d) => d.dateLabel === 'Yesterday') ?? null
    const prior = days.filter((d) => d.dateLabel !== 'Yesterday')
    const name = day?.machineName ?? prior[0]?.machineName ?? `Machine ${id}`

    return (
      <>
        <NavBar />
        <PageWrapper>
          <Flex center gap='0.75rem'>
            <H1WithBack title={name} arrow onClick={() => navigate('/operations/analytics/machines')} />
            <DemoBadge />
          </Flex>
          <Flex mb='16'>
            <OtherText grey>{`Yesterday · ${win.windowHours} h window · ${win.caption}`}</OtherText>
          </Flex>
          <Flex mb='16'>
            <TabsContainer>
              {SHIFT_WINDOWS.map((w) => (
                <Tabs key={w.key} active={shiftKey === w.key} onClick={() => setShiftKey(w.key)}>
                  {w.label}
                </Tabs>
              ))}
            </TabsContainer>
          </Flex>
          {!day && prior.length === 0 ? (
            <NoData text='No timeline recorded for this machine.' />
          ) : (
            <Flex column gap='1rem' width='100%'>
              <LiveTodaySection machineDay={liveTodayMachineDay(name)} />
              {day && (
                <Container>
                  <ContainerHeader>
                    <TitleText>Yesterday</TitleText>
                  </ContainerHeader>
                  <ContainerContent gap='0.75rem'>
                    {day.segments.length ? (
                      <DayStrip
                        segments={day.segments}
                        dateLabel={`${day.dateLabel} · ${day.shiftName}${day.operator ? ` · ${day.operator}` : ''}`}
                        startHour={win.startHour}
                        windowHours={win.windowHours}
                      />
                    ) : (
                      <OtherText grey>No detailed timeline recorded for yesterday — totals only.</OtherText>
                    )}
                    <BucketLegend />
                  </ContainerContent>
                </Container>
              )}
              {day && (
                <Container>
                  <ContainerHeader>
                    <TitleText>Totals</TitleText>
                  </ContainerHeader>
                  <ContainerContent>
                    <TotalsWrap>
                      <BucketRows>
                        {day.hoursByBucket.map((entry) => (
                          <BucketRow key={entry.bucket}>
                            <StatusPill status={LOSS_BUCKET_META[entry.bucket].tone}>
                              {LOSS_BUCKET_META[entry.bucket].label}
                            </StatusPill>
                            <BodyText weight='600'>{entry.hours.toFixed(1)} h</BodyText>
                          </BucketRow>
                        ))}
                      </BucketRows>
                      <StatGrid>
                        {machineStats(day).map((stat) => (
                          <StatCell key={stat.label}>
                            <OtherText grey>{stat.label}</OtherText>
                            <BodyText weight='600'>{stat.value}</BodyText>
                          </StatCell>
                        ))}
                      </StatGrid>
                    </TotalsWrap>
                    {day.shiftSplit && <OtherText grey>{day.shiftSplit}</OtherText>}
                  </ContainerContent>
                </Container>
              )}
              {prior.length > 0 && (
                <TimelineHistory
                  variant='machine'
                  machineDays={prior}
                  startHour={win.startHour}
                  windowHours={win.windowHours}
                />
              )}
            </Flex>
          )}
        </PageWrapper>
      </>
    )
  }

  const dayRows =
    selectedDay === 'Today'
      ? TODAY_MACHINE_DAYS
      : OPS_MACHINE_DAYS.filter((d) => d.dateLabel === selectedDay)
  const sorted = [...dayRows].sort((a, b) => a.utilizationPct - b.utilizationPct)
  const worstPct = sorted.length ? sorted[0].utilizationPct : 0
  const rows: IOpsMachineRow[] = sorted.map((d) => ({
    ...d,
    id: d.machineId,
    worst: d.utilizationPct === worstPct,
  }))

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem'>
          <Head1>Machine timelines</Head1>
          <DemoBadge />
        </Flex>
        <Flex mb='16'>
          <OtherText grey>
            {selectedDay === 'Today'
              ? `Today · 06:00 → ${OPS_TODAY_NOW_LABEL} — in progress`
              : `${selectedDay} · 06:00 → 06:00`}
          </OtherText>
        </Flex>
        <Flex mb='16' center gap='0.75rem' wrap>
          <TabsContainer>
            {QUICK_DAYS.map((label) => (
              <Tabs key={label} active={selectedDay === label} onClick={() => selectQuickDay(label)}>
                {label}
              </Tabs>
            ))}
          </TabsContainer>
          <CalendarInput
            name='timelineDay'
            placeholder='Jump to date'
            width='12rem'
            nomarg
            defaultValue={pickedDate}
            minDate={PICKER_MIN_DATE}
            maxDate={new Date()}
            onChange={handlePickDate}
          />
        </Flex>
        <HonestTable
          columns={machineColumns}
          data={rows}
          keyField='id'
          emptyMessage='No records for this day.'
          onClick={(item) => navigate(`/operations/analytics/machines/${item.machineId}`)}
        />
      </PageWrapper>
    </>
  )
}
